import { Injectable, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { catchError, mergeMap, filter, map, switchMap, share, tap, take, shareReplay } from 'rxjs/operators';
import { Subscription, BehaviorSubject, Observable, of, lastValueFrom, AsyncSubject, Observer, combineLatest, forkJoin, Subject, throwError } from 'rxjs';
import { MessageService } from 'primeng/api';

import { gateCall } from '../../../Core/rxjs-utils';
import { SavedMentionSearchesApi } from '../../../api/mentions/saved-mention-searches.api';
import { GeographyQueryModel } from '../../../api/mentions/model/geography-query.model';
import { SearchCriteriaQuerySentimentsEnum } from '../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { CommonApiErrorModel } from '../../../api/genericApi/models/common-api-error.model';
import { SavedMentionApiErrorEnum } from '../../../api/mentions/enum/saved-mention-api-error.enum';
import { SavedMentionSearchListItemApiModel } from '../../../api/mentions/model/saved-mention-search-list-item-api.model';
import { SavedMentionSearchApiModel } from '../../../api/mentions/model/saved-mention-search.api.model';
import { MultiBaseAbstractApi } from '../../../api/genericApi/multi-base-abstract.api';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { JournodexRegionsService } from '../../../Common/Journodex/services/journodex-regions.service';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { LeftSidebarListItemButton } from '../../../Common/Layout/models/left-sidebar-list-item-button.model';
import { DuplicatesStatusEnum } from '../../../Common/Filter/enums/duplicates-status.enum';
import { SourceGroup } from '../../../Common/Sources/models/source-group.model';
import { SourceGroupService } from '../../../Common/Sources/services/source-group.service';
import { FilterListProviderServiceInterface } from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { MonitorConfigService } from '../../../Common/MonitorShared/services/monitor-config.service';
import { monitoringSortTypeToSearchCriteriaSortFieldMap } from '../../../Common/MonitorShared/enum/monitoring-sort-types.enum';
import { CurrentResourceAbstractService } from '../../../Common/Basic/service/current-resource-abstract.service';
import { ResourceAction } from '../../../Common/Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '../../../Common/Basic/enums/resource-actions.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationReplacementsModel } from '../../../Core/Localization/models/localization-replacements.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';
import { UserPreferencesService } from '../../../Account/UserPreferences/services/user-preferences.service';
import { MentionSearchListItemModel } from '../../Shared/models/mention-search-list-item.model';
import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { MentionSearchQueryModel } from '../../Shared/models/mention-search-query.model';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { MediaTypeModel } from '../../Shared/models/media-type.model';
import { SentimentModel } from '../../Shared/models/sentiment.model';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { MonitoringRoutingService } from './monitoring-routing.service';
import { MentionSearchService } from './mention-search.service';
import { SearchResultsService } from './search-results.service';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { TextInputDialogComponent } from '../../../Common/Dialogs/components/TextInputDialog/text-input-dialog.component';
import { TextInputDialogModel } from '../../../Common/Dialogs/models/text-input-dialog.model';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { TargetingSearchesApiService } from '../../../api/targeting/targeting-searches.api.service';
import { DisplayGroupsService } from '../../../Common/Layout/services/display-groups.service';
import { NaturalLanguageWorkflowResponseApiModel } from '../../../api/mentions/model/natural-language-workflow-response-api.model';
import { AgentsApiService } from '../../../api/agents/agents.api';
import { AIContextTakeoverComponent } from '../../../Common/Dialogs/components/AIContextTakeover/ai-context-takeover.component';
import { ProgressStageModel } from '../../../Common/Dialogs/models/progress.model';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { MediaTypeEnum } from '../../../Common/Basic/enums/media-type.enum';
import { BroadcastSubTypeEnum } from '../../../Common/Basic/enums/broadcast-sub-type.enum';

/**
 * Used by the MonitoringRouter to determine when the route mode changes
 */
enum TopicMode {
    topics,
    other
}

/**
 * This service is responsible for managing saved-mention-searches (aka Topics)
 * Creating, updating, deleting, reseting topics
 * Storing topics lists, the focused topic, caching the focused topic, tracking the dirty state, etc.
 * Note: Topics (saved-mention-searches) can only be constructed on new content, not saved content.
 * Dev Note: This service is similar to targeting-searches.service. Ideally this service should be renamed
 * to monitor-searches.service since it deals with both unsaved searches and saved searches (topics).
 */
@Injectable()
export class TopicsService
    extends CurrentResourceAbstractService <MentionSearchModel, SavedMentionSearchApiModel, MentionSearchListItemModel, SavedMentionSearchListItemApiModel>
    implements OnDestroy, FilterListProviderServiceInterface {
    public readonly numberOfTagsPerTopic: number = 1;
    public maxTaggingTopics: number = 0;
    public maxAutoSavingTopics: number = 0;

    protected subscriptions: Subscription[] = [];

    private cachedTopic: MentionSearchModel;
    private focusedTopic: MentionSearchModel = null;
    private pendingAiTopic: MentionSearchModel = null;
    private topics: MentionSearchListItemModel[] = null;

    private isDirty: boolean;
    private isDirty$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private updateVisibilitySubject = new Subject<{id: number; isHidden: boolean; button: LeftSidebarListItemButton}>();

    currentResource$ = this.currentResourceSubject.asObservable().pipe(
        tap(topic => {
            this.focusedTopic = topic;
            this.generateSourceGroupDisplayString();
        }),
        shareReplay(1)
    );

    updateVisibility$ = this.updateVisibilitySubject.asObservable().pipe(
        switchMap(({id, isHidden, button}) => {
            button.loading = true;
            return this.savedMentionSearchesApi.getById(id).pipe(
                switchMap(savedSearch => {
                    savedSearch.hidden = isHidden;
                    return this.savedMentionSearchesApi.update(savedSearch);
                }),
                tap(() => {
                    this.refreshAllSubject.next();
                    button.loading = false;
                } ),
                catchError((error: HttpErrorResponse) => {
                    this.actionCompletedSubject.next({
                        actionType: ResourceActionTypes.UPDATE,
                        error
                    });
                    button.loading = false;
                    return of(null);
                })
            );
        })
    );

    private taggingTopicsCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private taggingTopicsLimitReached$: Observable<boolean> = this.taggingTopicsCountSubject.pipe(map(count => count >= this.maxTaggingTopics));
    private autoSavingTopicsCountSubject: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private autoSavingLimitReached$: Observable<boolean> = this.autoSavingTopicsCountSubject.pipe(map(count => count >= this.maxAutoSavingTopics));

    private currentRoute: MonitoringRouteModel;

    private regionsRequest: Subscription;
    private sourceGroups: SourceGroup[];

    private defaultMediaTypes: MediaTypeModel[] = [];

    private tagToTopicsMap$ = this.all$.pipe(
        // Maps tagIds to topics they are applied on
        map(topics => {
            if (topics && topics.length > 0) {
                const tagToTopicsMap = new Map<number, MentionSearchListItemModel[]>();
                topics.forEach(topic => {
                    if (!topic.applyTagIds || !topic.applyTagIds.length) {
                        return;
                    }

                    topic.applyTagIds.forEach(tagId => {
                        let topicsWithTag = tagToTopicsMap.get(tagId);

                        if (!topicsWithTag) {
                            topicsWithTag = [];
                            tagToTopicsMap.set(tagId, topicsWithTag);
                        }
                        topicsWithTag.push(topic);
                        tagToTopicsMap.set(tagId, topicsWithTag);
                    });
                });
                return tagToTopicsMap;
            }
        }),
        shareReplay(1)
    );

    all$ = this.refreshAllSubject.asObservable().pipe(
        switchMap(() => gateCall(this.currentUserStateService.hasMonitoringAccess()).pipe(
            switchMap(() => this.savedMentionSearchesApi.getList()),
            map(savedMentionSearches => {
                const mentionSearches: MentionSearchListItemModel[] = savedMentionSearches.map(
                    (apiModel) => new MentionSearchListItemModel(apiModel)
                );
                this.topics = mentionSearches;
                this.taggingTopicsCountSubject.next(savedMentionSearches.filter(topic => topic.applyTagIds.length > 0).length);
                this.autoSavingTopicsCountSubject.next(savedMentionSearches.filter(topic => topic.savingEnabled).length);
                return mentionSearches;
            }))
        )
    );

    constructor(
        private savedMentionSearchesApi: SavedMentionSearchesApi,
        protected coreDialogService: CoreDialogService,
        private mentionSearchService: MentionSearchService,
        private monitoringRoutingService: MonitoringRoutingService,
        private searchResultsService: SearchResultsService,
        private journodexService: JournodexRegionsService,
        private messageService: MessageService,
        private monitoringConfigService: MonitorConfigService,
        private sourceGroupService: SourceGroupService,
        public websocketEvents: WebsocketAwareNeptuneService,
        private currentUserStateService: CurrentUserStateService,
        private userPreferencesService: UserPreferencesService,
        private targetingSearchesApiService: TargetingSearchesApiService,
        private displayGroupsService: DisplayGroupsService,
        private agentsApiService: AgentsApiService
    ) {
        super();

        this.subscriptions.push(
            combineLatest([
                this.monitoringRoutingService.getModeChange(this.currentMode$()).pipe(
                    filter(mode => mode.isModeChange),
                    switchMap(() => this.userPreferencesService.getUserAppPreferences().pipe(
                        map(userAppPrefs => userAppPrefs.monitoringPrefs.topicsPrefs.defaultDisplaySettings)
                    ))
                ),
                this.monitoringRoutingService.getCurrentRoute().pipe(filter(x => !!x)),
                this.monitoringConfigService.getAvailableMediaTypes().pipe(filter(x => x.length > 0)),
            ]).subscribe(async ([displayPrefs, route, provisionedMediaTypes]) => {
                this.currentRoute = route;
                this.defaultMediaTypes = MediaTypeModel.getAllMediaTypes().filter(mediaType => {
                    return !mediaType.isSocial && provisionedMediaTypes.find(x => x === mediaType.id);
                });

                const id: number = +route.value;
                let topic: MentionSearchModel = null;
                let performSearch: boolean = false;

                if ((route.type === RouteType.topicAnalyticsSyndicates) ||
                    (route.type === RouteType.adHocAnalyticsSyndicates) ||
                    (route.type === RouteType.allSavedCoverageAnalyticsSyndicates) ||
                    (route.type === RouteType.folderAnalyticsSyndicates) ||
                    (route.type === RouteType.subscriptionAnalyticsSyndicates)
                ) {
                    this.mentionSearchService.setShowSyndicates(false);
                } else {
                    this.mentionSearchService.setShowSyndicates(true);
                }

                if (!this.mentionSearchService.showSyndicates && (displayPrefs.pageSize > 100)) {
                    this.mentionSearchService.setPageSize(100);
                } else {
                    this.mentionSearchService.setPageSize(displayPrefs.pageSize);
                }
                this.mentionSearchService.setSortField(monitoringSortTypeToSearchCriteriaSortFieldMap.get(displayPrefs.sortType));
                this.mentionSearchService.setSortOrder(displayPrefs.sortDirection);

                switch (route.type) {
                    case RouteType.topic:
                    case RouteType.topicAnalyticsDashboard:
                    case RouteType.topicAnalyticsSyndicates:
                        if (!id) {
                            this.monitoringRoutingService.gotoTopics();
                            return;
                        }
                        if (this.focusedTopic && id === this.focusedTopic.id) {
                            topic = this.focusedTopic;
                        } else {
                            try {
                                topic = await lastValueFrom(this.getTopic(id));
                            } catch (err) {
                                // id no longer exists, provide some user feedback before going to topics hype
                                this.monitoringRoutingService.gotoTopics({
                                    queryParams: { notFound: 'true' }
                                });
                                return;
                            }
                        }
                        performSearch = true;
                        break;
                    case RouteType.search:
                        if (this.pendingAiTopic) {
                            topic = this.pendingAiTopic;
                            this.pendingAiTopic = null;
                        } else if (route.queryParamMap && route.queryParamMap.has('copy')) {
                            try {
                                topic = await lastValueFrom(this.getTopic(+route.queryParamMap.get('copy')));
                                topic.anonymize();
                            } catch (err) {
                                // id no longer exists, go to plain search
                                this.monitoringRoutingService.gotoSearch();
                                return;
                            }
                        } else {
                            topic = this.getDefaultTopic();
                        }
                        break;
                    case RouteType.updateSearch:
                        topic = this.focusedTopic;
                        if (!topic) {
                            this.monitoringRoutingService.gotoSearch();
                            return;
                        } else if (topic.id) {
                            // A saved topic is now edited in the takeover panel rather than the
                            // (removed) update-topic route.
                            this.openEditTopicPanel(topic);
                            return;
                        }
                        break;
                    case RouteType.results:
                    case RouteType.adHocAnalyticsDashboard:
                    case RouteType.adHocAnalyticsSyndicates:
                        if (this.pendingAiTopic) {
                            topic = this.pendingAiTopic;
                            this.pendingAiTopic = null;
                        } else {
                            topic = this.focusedTopic;
                        }
                        performSearch = true;
                        const filtersQueryString = route.queryParamMap.get('filters');
                        if (!topic) {
                            if (filtersQueryString) {
                                // TODO : Works as long as you don't click "edit search"
                                // then it seems to get in a weird inconsistent state
                                this.mentionSearchService.setSearchCriteria(
                                    MentionSearchCriteriaModel.fromFilterQueryString(filtersQueryString)?.search
                                );
                            } else {
                                this.monitoringRoutingService.gotoSearch();
                            }
                            return;
                        } else if (topic.id) {
                            this.monitoringRoutingService.gotoTopic(topic.id);
                            return;
                        }
                        break;
                    default:
                        topic = null;
                }

                if (topic && (!this.cachedTopic || !topic.id || this.cachedTopic.id !== topic.id)) {
                    this.cachedTopic = topic.clone(false, true);
                }

                if (topic) {
                    const isFiltersLocked = this.searchResultsService.filtersLocked();
                    const isViewModeChanged = this.searchResultsService.getIsViewModeChange();
                    const filtersQueryString = route.queryParamMap.get('filters');
                    this.mentionSearchService.setSearchCriteria(topic.searchCriteria);
                    if (topic !== this.focusedTopic) {
                        this.setCurrentResource(topic);
                    }
                    if (!isFiltersLocked) {
                        if (
                            topic.searchCriteria.isAccountSearch
                            || (
                                topic.searchCriteria.query.dateQuery
                                && !DateQueryModel.equals(topic.searchCriteria.query.dateQuery, DateQueryModel.getAllQuery())
                            )
                        ) {
                            this.mentionSearchService.setDateQuery(topic.searchCriteria.query.dateQuery);
                        } else if (filtersQueryString) {
                            // use date query from filter of route params
                            const dateQuery = MentionSearchCriteriaModel.fromFilterQueryString(filtersQueryString)?.search.query.dateQuery;
                            if (dateQuery) {
                                this.mentionSearchService.setDateQuery(dateQuery);
                            }
                        } else if (!isViewModeChanged) {
                            this.mentionSearchService.setDateQuery(DateQueryModel.getLast24HoursQuery());
                            this.mentionSearchService.clearFilters();
                        }
                        if (!isViewModeChanged) {
                            this.mentionSearchService.setDuplicateFilter(
                                topic.searchCriteria.showSimilar ? DuplicatesStatusEnum.show : DuplicatesStatusEnum.hide
                            );
                        }
                    } else {
                        this.searchResultsService.loadLockedFiltersFromSession();
                    }
                    if (performSearch) {
                        this.mentionSearchService.clearSearch(!(isViewModeChanged || isFiltersLocked));
                        this.mentionSearchService.search(true, true);
                    }
                } else {
                    this.setCurrentResource(null);
                }
            }),
            this.monitoringConfigService.getTaggingTopicLimit().subscribe(
                taggingTopicLimit => {
                    this.maxTaggingTopics = taggingTopicLimit;
                }
            ),
            this.monitoringConfigService.getAutoSavingTopicLimit().subscribe(
                autoSavingTopicLimit => {
                    this.maxAutoSavingTopics = autoSavingTopicLimit;
                }
            ),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x))
                .subscribe(
                    message => {
                        if (message.resourceType === ResourceType.savedMentionSearches) {
                            this.refreshAllSubject.next();
                        }
                    }
                ),
            this.sourceGroupService.all$.subscribe(x => {
                this.sourceGroups = x;
                this.generateSourceGroupDisplayString();
            }),
            this.updateVisibility$.subscribe(),
            this.actionCompleted$.subscribe(()=> {
                this.refreshAllSubject.next();
            }),
        );
    }

    getApiService(): MultiBaseAbstractApi<SavedMentionSearchApiModel, SavedMentionSearchListItemApiModel> {
        return this.savedMentionSearchesApi;
    }
    constructListModel(listApiModel: SavedMentionSearchListItemApiModel): MentionSearchListItemModel {
        return new MentionSearchListItemModel(listApiModel);
    }
    constructResourceModel(apiModel: SavedMentionSearchApiModel): MentionSearchModel {
        return new MentionSearchModel(apiModel);
    }

    get getCachedTopic(): MentionSearchModel {
        return this.cachedTopic;
    }


    getDirtyFlag(): Observable<boolean> {
        return this.isDirty$;
    }

    setDirtyFlag(state: boolean) {
        this.isDirty = state;
        this.isDirty$.next(state);
    }

    private getDefaultTopic(): MentionSearchModel {
        const topic = new MentionSearchModel();
        topic.searchCriteria.query.includedMediaTypes = this.defaultMediaTypes;

        // Add TV Program subtype to default topic search criteria
        const tvMediaType = this.defaultMediaTypes.find(x => x.id === MediaTypeEnum.TV);
        if (tvMediaType) {
            topic.searchCriteria.query.includedTvTypes.push(new MediaTypeModel(MediaTypeEnum.TV, BroadcastSubTypeEnum.PROGRAM));
        }

        topic.searchCriteria.query.sentiments = [
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive),
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative),
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral),
            new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable),
        ];

        this.cachedTopic = topic.clone(false);

        return topic;
    }

    /**
     * Gets the saved searches
     */
    getTopics(): Observable<MentionSearchListItemModel[]> {
        return this.all$;
    }

    getFilterListItems(): Observable<AuditableModel[]> {
        return this.all$;
    }

    /**
     * Gets the saved searches
     */
    getFocusedTopic(): Observable<MentionSearchModel> {
        return this.currentResource$;
    }

    resetTopicToDefault() {
        this.setCurrentResource(this.getDefaultTopic());
    }

    resetTopicToCache() {
        if (!this.cachedTopic || !this.focusedTopic) {
            return;
        }
        this.setCurrentResource(this.cachedTopic.clone(false, true));
    }

    /**
     * Opens the edit-topic takeover panel for an existing saved search instead of navigating to the
     * update-topic route. Accepts either a fully-loaded topic or its id. Seeds the focused/cached
     * topic and clears the dirty flag so the hosted editor's form, dirty-diffing and Undo behave
     * exactly as they did on the route.
     *
     * Opened via the string-keyed dialog map ({@link BootstrapCoreDialogMap}) to avoid a circular
     * dependency between this service and the panel component (which lives in MonitoringSharedModule).
     */
    /**
     * Edits a topic from the left-hand panel: if it's already the topic being viewed, opens the
     * editor straight away; otherwise navigates to that topic first (so the underlying page — results,
     * RHP, summary — reflects the topic being edited) and opens the editor once navigation settles.
     */
    gotoTopicThenEdit(id: number): void {
        if (this.currentRoute?.type === RouteType.topic && +this.currentRoute.value === id) {
            this.openEditTopicPanel(id);
            return;
        }

        this.monitoringRoutingService.gotoTopic(id);
        this.monitoringRoutingService.getCurrentRoute().pipe(
            filter(route => route?.type === RouteType.topic && +route.value === id),
            take(1)
        ).subscribe(() => this.openEditTopicPanel(id));
    }

    openEditTopicPanel(topicOrId: MentionSearchModel | number): void {
        const id = typeof topicOrId === 'number' ? topicOrId : topicOrId.id;

        // If we're editing the topic that's already focused, keep its in-memory state (including any
        // unsaved changes, the existing saved baseline and the dirty flag) so the dirty indicator /
        // Save button persist across re-opens. Otherwise load it fresh from the server (or use the
        // supplied model) and start from a clean baseline.
        const isFocusedTopic = this.focusedTopic
            && (topicOrId === this.focusedTopic || (!!id && this.focusedTopic.id === id));

        if (isFocusedTopic) {
            this.openEditTopicPanelForTopic(this.focusedTopic, false);
        } else if (typeof topicOrId === 'number') {
            this.getTopic(topicOrId).subscribe(topic => this.openEditTopicPanelForTopic(topic, true));
        } else {
            this.openEditTopicPanelForTopic(topicOrId, true);
        }
    }

    private openEditTopicPanelForTopic(topic: MentionSearchModel, resetBaseline: boolean): void {
        // Establish a clean saved baseline only when loading fresh (or if we somehow don't have one
        // for this topic). When re-editing a topic that already has unsaved changes, keep the existing
        // baseline and dirty state so the dirty indicator persists.
        if (resetBaseline || this.cachedTopic?.id !== topic.id) {
            this.cachedTopic = topic.clone(false, true);
            this.setDirtyFlag(false);
        }

        // Work on an independent clone so editing the form never mutates the caller's topic object
        // (e.g. the results page's focused topic) or the cached baseline used for Undo / discard.
        this.setCurrentResource(topic.clone(false, true));

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        this.coreDialogService.open('EditTopicTakeOverPanelComponent', {
            inputs: {
                overlayContainer: document.getElementById('main-content'),
                animationTrigger
            }
        });
    }

    /**
     * Applies a search's criteria to the mention search and runs it. Used by the edit-topic takeover
     * panel's Search action: the panel is typically open over the very results route the search would
     * navigate to, where router navigation is a no-op and would not re-run the search.
     */
    applyTopicSearch(search: MentionSearchModel): void {
        this.mentionSearchService.setSearchCriteria(search.searchCriteria);
        // The duplicates (hide-similar) filter is intentionally NOT set here: it is a runtime filter
        // that is not persisted on the topic, so applying/saving a topic must preserve the user's
        // current filter selection. Search explicitly sets it from the form before this runs.
        this.mentionSearchService.clearSearch(true);
        this.mentionSearchService.search(true, true);
    }


    getTopic(id: number): Observable<MentionSearchModel> {
        return this.savedMentionSearchesApi.getById(id).pipe(
            switchMap(apiSearch => this.getTopicFromApiModel(apiSearch)),
            map(topic => {
                // @HACK remove the publication date of the topic if it exists
                topic.searchCriteria.query.dateQuery = null;
                return topic;
            })
        );
    }

    getTopicFromApiModel(savedMentionsearchApiModel: SavedMentionSearchApiModel): Observable<MentionSearchModel> {
        if (this.regionsRequest) {
            this.regionsRequest.unsubscribe();
        }
        let topic: MentionSearchModel;

        return combineLatest([
            this.monitoringConfigService.getAvailableMediaTypes().pipe(filter(x => x.length !== 0 || !!x)),
            this.monitoringConfigService.getAvailableSocialMediaTypes().pipe(filter(x => x.length !== 0 || !!x))
        ]).pipe(
            take(1),
            map(([provisionedMediaTypes, provisionedSocialMediaTypes]) => {
                savedMentionsearchApiModel.query.mediaTypeFilter.include = savedMentionsearchApiModel.query.mediaTypeFilter.include.filter(item =>
                    provisionedMediaTypes.includes(item) || provisionedSocialMediaTypes.includes(item)
                );
                topic = new MentionSearchModel(savedMentionsearchApiModel);
                if (topic?.searchCriteria.query.sentiments.length === 0) {
                    topic.searchCriteria.query.sentiments = [
                        new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive),
                        new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative),
                        new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral),
                        new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable),
                    ];
                }
            }),
            mergeMap(() => this.mapRawRegionsToComplexRegions(topic)),
            map(regionInfo => {
                this.updateMentionSearchGeographicInformation(
                    regionInfo,
                    topic
                );

                return topic;
            })
        );
    }

    navigateToNewSearchWithTopic(apiTopic: SavedMentionSearchApiModel): Observable<void> {
        // Set minimum fields
        if (!apiTopic.query.mediaTypeFilter) {
            apiTopic.query.mediaTypeFilter = {
                include: [],
                exclude: []
            };
        }

        return this.getTopicFromApiModel(apiTopic).pipe(
            tap(topic => {
                this.pendingAiTopic = topic;
                this.monitoringRoutingService.gotoResults({
                    queryParams: { selectedTab: 'searchSummary' }
                });
            }),
            map(() => undefined),
            catchError(err => {
                if (err) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: this.localizationService.transform('Monitoring.topics.loadTopicErrorHeading'),
                        detail: this.localizationService.transform('Monitoring.topics.loadTopicErrorMessage')
                    });
                }
                return throwError(() => err);
            }),
        );
    }

    openTopicAgentDialog(
        naturalLanguagePrompt: string,
        taskDescription: string,
        systemPrompt: string | null,
        topicSystemPrompt: string | null,
        topicApplyFn: (topic: SavedMentionSearchApiModel) => Observable<void>,
        onDialogClose?: (refinedPurpose: string) => void,
        progressStages?: ProgressStageModel[]
    ): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        this.coreDialogService.open(AIContextTakeoverComponent, {
            inputs: {
                title: 'Monitoring.search.agilityAgent.contextTakeoverTitle',
                originalPurposeLabel: 'Monitoring.search.prcopilot.originalPurpose',
                refinedPurposeLabel: 'Monitoring.search.prcopilot.refinedPurpose',
                naturalLanguagePrompt,
                taskDescription,
                systemPrompt,
                overlayContainer: document.getElementById('main-content'),
                animationTrigger,
                progressStages,
                applyFn: (refinedPurpose: string) => this.agentsApiService.createTopic(refinedPurpose, topicSystemPrompt || null).pipe(
                    mergeMap(response => {
                        response.topic.searchIntent = refinedPurpose;
                        if (response.topic.query.mediaTypeFilter?.include?.length) {
                            return topicApplyFn(response.topic as unknown as SavedMentionSearchApiModel);
                        }
                        return combineLatest([
                            this.monitoringConfigService.getAvailableMediaTypes().pipe(take(1)),
                            this.monitoringConfigService.getAvailableSocialMediaTypes().pipe(take(1))
                        ]).pipe(
                            mergeMap(([mediaTypes, socialMediaTypes]) => {
                                response.topic.query.mediaTypeFilter = {
                                    include: [...mediaTypes, ...socialMediaTypes],
                                    exclude: response.topic.query.mediaTypeFilter?.exclude ?? []
                                };
                                return topicApplyFn(response.topic as unknown as SavedMentionSearchApiModel);
                            })
                        );
                    })
                )
            },
            outputs: {
                dialogClose: (refinedPurpose: string) => {
                    if (onDialogClose && refinedPurpose) {
                        onDialogClose(refinedPurpose);
                    }
                }
            }
        });
    }

    refineTopicWithAgent(topic: MentionSearchModel, prompt: string): Observable<void> {
        return this.agentsApiService.updateTopic(topic.toApiModel(), prompt).pipe(
            tap(response => {
                const updatedTopic = topic.clone(false);
                updatedTopic.searchCriteria.query = new MentionSearchQueryModel(response.topic.query);
                if (response.topic.searchIntent) {
                    updatedTopic.searchIntent = response.topic.searchIntent;
                }
                this.setCurrentResource(updatedTopic);
                this.setDirtyFlag(true);
                this.mentionSearchService.setSearchCriteria(updatedTopic.searchCriteria);
            }),
            map(() => void 0),
            catchError(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Monitoring.topics.updateTopicErrorHeading'),
                    detail: this.localizationService.transform('Monitoring.topics.updateTopicErrorMessage')
                });
                return throwError(() => new Error());
            })
        );
    }

    updateTopic(topic: MentionSearchModel): Observable<MentionSearchModel> {
        // @HACK unlike other saved searches, topics should not be allowed to save their publication date ranges
        const apiTopic = topic.toApiModel();
        delete apiTopic.query.publicationDate;
        // hide-similar (showSimilar) is a runtime filter and is not persisted at the topic level
        // (saved-search filters for reports/briefings still persist it via toApiModel).
        delete apiTopic.showSimilar;

        if (topic.searchCriteria.query.discoverListFilter?.filterSearchId
            &&  topic.searchCriteria.query.discoverListFilter?.filterSearchId !== this.cachedTopic.searchCriteria.query.discoverListFilter?.filterSearchId
        ){
            this.targetingSearchesApiService.removeExpiryOfSearch(topic.searchCriteria.query.discoverListFilter.filterSearchId).subscribe();
        }
        return this.updateResourceFromApiModel(topic, {actionType: ResourceActionTypes.UPDATE, refreshResource: true}, true, apiTopic).pipe(
            mergeMap(() => {
                this.cachedTopic = topic.clone(false, true);
                this.setDirtyFlag(false);
                return this.getTopic(topic.id);
            }),
            share({
                connector: () => new AsyncSubject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
    }

    /**
     * Create a topic (saved-mention-search), setting the focused topic and handling any errors.
     * Topics can't save their publication date ranges
     * @param navigateToResults Toggle for navigating to newly saved topics
     * @returns Observable that must be subscribed to
     */
    createTopic(topic: MentionSearchModel, navigateToResults: boolean = false): Observable<MentionSearchModel> {
        // @HACK unlike other saved searches, topics should not be allowed to save their publication date ranges
        const apiTopic = topic.toApiModel();
        delete apiTopic.query.publicationDate;
        // hide-similar (showSimilar) is a runtime filter and is not persisted at the topic level
        // (saved-search filters for reports/briefings still persist it via toApiModel).
        delete apiTopic.showSimilar;
        if (topic.searchCriteria.query.discoverListFilter?.filterSearchId
            && topic.searchCriteria.query.discoverListFilter?.filterSearchId !== this.cachedTopic.searchCriteria.query.discoverListFilter?.filterSearchId
        ){
            this.targetingSearchesApiService.removeExpiryOfSearch(topic.searchCriteria.query.discoverListFilter.filterSearchId).subscribe();
        }
        return this.createResourceFromApiModel(topic, {actionType: ResourceActionTypes.CREATE, refreshResource: true}, true, apiTopic).pipe(
            mergeMap(id => {
                return this.getTopic(id);
            }),
            share({
                connector: () => new AsyncSubject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            }),
            map(savedTopic => {
                this.cachedTopic = savedTopic.clone(false, true);
                this.setDirtyFlag(false);
                this.setCurrentResource(savedTopic);
                if (navigateToResults) {
                    // Creating a new topic (e.g. "Save search as topic") navigates straight to the
                    // new topic's results rather than opening the edit takeover panel.
                    this.monitoringRoutingService.gotoTopic(savedTopic.id);
                }
                return savedTopic;
            })

        );
    }

    public createTopicFromNaturalLanguagePrompt(name: string, prompt: string): Observable<NaturalLanguageWorkflowResponseApiModel> {
        const requestModel = {
            userMessage: prompt,
            createTopic: true,
            topicName: name || 'Natural Language - ' + new Date().toLocaleString(),
        };
        return this.savedMentionSearchesApi.generateSearchCriteriaFromNaturalLanguagePrompt(requestModel);
    }

    public createTopicUsingAgentsService(name: string, prompt: string): Observable<NaturalLanguageWorkflowResponseApiModel> {
        return this.agentsApiService.createTopic(prompt).pipe(
            switchMap(agentResponse => {
                return this.savedMentionSearchesApi.getList().pipe(
                    switchMap(existingTopics => {
                        const existingNames = new Set(existingTopics.map(t => t.name));
                        let uniqueName = name || 'AI Topic - ' + new Date().toLocaleString();
                        let counter = 2;
                        while (existingNames.has(uniqueName)) {
                            uniqueName = `${name} ${counter}`;
                            counter++;
                        }

                        const searchToSave: SavedMentionSearchApiModel = {
                            query: agentResponse.topic.query,
                            sortField: agentResponse.topic.sortField,
                            sortOrder: agentResponse.topic.sortOrder,
                            // hide-similar is a runtime filter, not persisted on the topic; agent topics
                            // always start from the default (show similar).
                            showSimilar: true,
                            searchIntent: agentResponse.topic.searchIntent,
                            name: uniqueName,
                        };

                        return this.savedMentionSearchesApi.create(searchToSave).pipe(
                            map(topicId => {
                                const savedModel: SavedMentionSearchApiModel = { ...searchToSave, id: topicId };
                                const result: NaturalLanguageWorkflowResponseApiModel = {
                                    topicId,
                                    topicName: uniqueName,
                                    booleanSearch: agentResponse.topic?.query?.advancedBooleanExpressionFilter?.filter ?? '',
                                    savedSearchModel: savedModel,
                                };
                                return result;
                            }),
                            tap(() => this.refreshAllSubject.next())
                        );
                    })
                );
            })
        );
    }

    public deleteTopic(topic: MentionSearchModel|MentionSearchListItemModel): Observable<number> {
        return this.deleteResource(topic.id).pipe(
            share({
                connector: () => new AsyncSubject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
    }

    messageCenter(actionResult: ResourceAction): void {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as MentionSearchModel;

        const replacement = {} as LocalizationReplacementsModel;
        replacement.name = resource?.name;

        let summary;
        let detail;
        let severity = messageSeverityEnum.success;

        // success
        switch (actionType) {
            case ResourceActionTypes.CREATE:
                summary = 'Monitoring.topics.saveTopicSuccessHeading';
                detail = 'Monitoring.topics.saveTopicSuccessMessage';
                break;
            case ResourceActionTypes.UPDATE:
                summary = 'Monitoring.topics.updateTopicSuccessHeading';
                detail = 'Monitoring.topics.updateTopicSuccessMessage';
                break;
            case ResourceActionTypes.DELETE:
                summary = 'Monitoring.topics.deleteTopicSuccessHeading';
                detail = 'Monitoring.topics.deleteTopicSuccessMessage';
        }
        // error
        if (error) {
            severity = messageSeverityEnum.error;
            summary = null;
            detail = null;
            switch (actionType) {
                case ResourceActionTypes.CREATE:
                    if (this.processCommonErrors(error.error, false, resource?.name)) {
                        summary = 'Monitoring.topics.saveTopicErrorHeading';
                        detail = 'Monitoring.topics.saveTopicErrorMessage';
                    }
                    break;
                case ResourceActionTypes.UPDATE:
                    if (this.processCommonErrors(error.error, true, resource?.name)) {
                        summary = 'Monitoring.topics.updateTopicErrorHeading';
                        detail ='Monitoring.topics.updateTopicErrorMessage';
                    }
                    break;
                case ResourceActionTypes.DELETE:
                    summary = 'Monitoring.topics.deleteTopicErrorHeading';
                    detail = 'Monitoring.topics.deleteTopicErrorMessage';
            }
        }

        if (summary || detail) {
            this.messageService.add({
                severity,
                summary: this.localizationService.transform(summary),
                detail: this.localizationService.transform(detail, replacement)
            });
        }
    }

    openRenameTopicDialog(search: MentionSearchListItemModel) {
        const io: TextInputDialogModel = {
            inputs: {
                id: 'renameTopic',
                heading: 'Monitoring.topics.renameTopicDialog.heading',
                description: 'Monitoring.topics.renameTopicDialog.description',
                placeholder: 'Monitoring.topics.renameTopicDialog.placeholder',
                initialValue: search.name,
                okLabel: 'Lhp.actions.rename',
                formValidator: (name: string) => {
                    if (this.topics.find(t => t.name === name.trim() && t.id !== search.id)) {
                        return [
                            'Monitoring.topics.saveTopicsDuplicateNameErrorMessage'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string) => {
                    if (name !== search.name) {
                        this.renameTopic(search.id, name);
                    }
                    return of(null);
                }
            }
        };

        this.coreDialogService.open(TextInputDialogComponent, io);
    }

    renameTopic(id: number, name: string) {
        this.savedMentionSearchesApi.getById(id).pipe(
            tap(savedSearch => savedSearch.name = name),
            switchMap(savedSearch => this.savedMentionSearchesApi.update(savedSearch)),
            tap(() => this.refreshAllSubject.next())
        ).subscribe({
            next: () => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Monitoring.topics.renameTopicSuccessHeading'),
                    detail: this.localizationService.transform('Monitoring.topics.renameTopicSuccessMessage', {
                        name: name
                    })
                });
                if (this.focusedTopic?.id === id) {
                    this.focusedTopic.name = name;
                    this.setCurrentResource(this.focusedTopic);
                }
            },
            error: (err) => {
                if (err && err.error) {
                    if (this.processCommonErrors(err.error, true, name)) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: this.localizationService.transform('Monitoring.topics.renameTopicErrorHeading'),
                            detail: 'Monitoring.topics.renameTopicErrorMessage'
                        });
                    }
                }
            }
        });
    }

    updateVisibility(id: number, action: ActionsEnum, button: LeftSidebarListItemButton) {
        this.updateVisibilitySubject.next({
            id: id,
            isHidden: action === ActionsEnum.hide,
            button: button
        });
    }


    bulkUpdateVisibility(ids: number[], action: ActionsEnum) {
        const hidden = action === ActionsEnum.hide;
        const observables: Observable<number>[] = [];
        this.savedMentionSearchesApi.getSavedSearchBulk(ids).pipe(
            map(batchModel => {
                ids.forEach(id => {
                    batchModel[id].hidden = hidden;
                    observables.push(this.savedMentionSearchesApi.update(batchModel[id]));
                });
            }),
            mergeMap(() => {
                return forkJoin(observables);
            })
        ).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Monitoring.topics.bulkUpdateTopicSuccessHeading'),
                    detail: this.localizationService.transform('Monitoring.topics.bulkUpdateTopicSuccessMessage', {
                        number: result.length
                    })
                });
                this.refreshAllSubject.next();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Monitoring.topics.bulkUpdateTopicErrorHeading'),
                    detail: this.localizationService.transform('Monitoring.topics.bulkUpdateTopicErrorMessage')
                });
                this.refreshAllSubject.next();
            }
        });
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    public confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'TopicsBulkDelete',
                    heading: 'Monitoring.bulkDeleteTopic',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Monitoring.confirmBulkDeleteTopic.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Monitoring.confirmBulkDeleteTopic.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDeleteTopics(ids);
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });
    }
    public bulkDeleteTopics(ids: number[]): void {
        const observables = ids.map(id => this.savedMentionSearchesApi.deleteById(id));
        forkJoin(observables).subscribe({
            next: (result) => {
                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform('Monitoring.topics.bulkDeleteTopicSuccessHeading'),
                    detail: this.localizationService.transform('Monitoring.topics.bulkDeleteTopicSuccessMessage', {number: result.length})
                });
                if (this.focusedTopic && ids.includes(this.focusedTopic.id)) {
                    this.monitoringRoutingService.gotoTopics();
                }
                this.refreshAllSubject.next();
            },
            error: (err) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Monitoring.topics.bulkDeleteTopicErrorHeading'),
                    detail: this.localizationService.transform('Monitoring.topics.bulkDeleteTopicErrorMessage')
                });
                this.refreshAllSubject.next();
            }
        });
    }

    /**
     * Adds provided topics to a group (or removes group ID if Uncategorized is selected)
     * @param topicIds
     * @param groupId Group ID or undefined if Uncategorized is selected
     */
    public updateGroup(topicIds: number[], group: DisplayGroupModel) {
        let request;

        if (group.id) {
            request = this.savedMentionSearchesApi.updateSavedSearchDisplayGroup(topicIds, [], group.id);
        } else {
            request = this.savedMentionSearchesApi.removeSavedSearchesFromDisplayGroup(topicIds);
        }

        request.subscribe({
            next: () => {
                this.refreshAllSubject.next();
                const hasFocusedTopicId = topicIds.includes(this.focusedTopic?.id);
                if (hasFocusedTopicId) {
                    this.focusedTopic.groupId = group.id;
                    this.setCurrentResource(this.focusedTopic);
                }
                if (topicIds.length === 1) {
                    const itemName = this.topics.find(subscription => subscription.id === topicIds[0]).name;
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.single',
                            {itemName: itemName, groupName: group.name}
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: this.localizationService.transform('Common.lhp.moveToGroup.success.summary'),
                        detail: this.localizationService.transform(
                            'Common.lhp.moveToGroup.success.detail.multiple',
                            {number: topicIds.length, name: group.name}
                        )
                    });
                }
            },
            error: (err) => {
                this.refreshAllSubject.next();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Removes group ID from all provided topics
     * @param ids topic IDs
     */
    public removeAllTopicsFromGroup(ids: number[]) {
        this.savedMentionSearchesApi.removeSavedSearchesFromDisplayGroup(ids).subscribe({
            next: () => {
                this.refreshAllSubject.next();
            }
        });
    }

    public updateFavorites(addIds: number[], removeIds: number[]) {
        this.savedMentionSearchesApi.updateSavedSearchFavorites(addIds, removeIds).subscribe({
            next: () => {
                this.refreshAllSubject.next();
            },
            error: (err) => {
                this.refreshAllSubject.next();
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Common.lhp.moveToGroup.error.summary'),
                    detail: this.localizationService.transform('Common.lhp.moveToGroup.error.detail')
                });
            }
        });
    }

    /**
     * Process error messages returned by API calls
     * @todo All the API calls in this service should process through this function for common error handling
     * @returns boolean If any errors reported by API are not processed here, return true
     */
    private processCommonErrors(errorBlock: CommonApiErrorModel, isUpdate: boolean, searchName: string): boolean {
        let unhandledErrors: boolean = false;

        if (errorBlock.details) {
            for (const errorDetail of errorBlock.details) {
                switch (errorDetail.code) {
                    case SavedMentionApiErrorEnum.duplicateEntity:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Monitoring.topics.saveTopicErrorHeading',
                            detail: 'Monitoring.topics.saveTopicsDuplicateNameErrorMessage'
                        });
                        break;
                    case SavedMentionApiErrorEnum.badReference:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Monitoring.topics.invalidReference',
                        });
                        break;
                    case SavedMentionApiErrorEnum.invalidModel:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Monitoring.topics.invalidSearchError',
                            detail: errorDetail.message
                        });
                        break;
                    case SavedMentionApiErrorEnum.noIdOnNew:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Monitoring.topics.error.badId',
                        });
                        break;
                    case SavedMentionApiErrorEnum.invalidBooleanQuery:
                        if (isUpdate) {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Monitoring.topics.updateBooleanTopicErrorHeading',
                                detail: this.localizationService.transform('Monitoring.topics.updateBooleanTopicErrorMessage', {
                                    name: searchName
                                })
                            });
                        } else {
                            this.messageService.add({
                                severity: messageSeverityEnum.error,
                                summary: 'Monitoring.topics.saveBooleanTopicErrorHeading',
                                detail: this.localizationService.transform('Monitoring.topics.saveBooleanTopicErrorMessage', {
                                    name: searchName
                                })
                            });
                        }
                        break;
                    case SavedMentionApiErrorEnum.tagLimitExceeded:
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'Monitoring.topics.taggingTopicsLimitExceeded.heading',
                            detail: this.localizationService.transform('Monitoring.topics.taggingTopicsLimitExceeded.message', {
                                name: searchName
                            })
                        });
                        break;
                    default:
                        unhandledErrors = true;
                        break;
                }
            }
        }

        return unhandledErrors;
    }


    confirmDeleteTopic(topic: MentionSearchModel|MentionSearchListItemModel, navigate: boolean = true) {
        const io: ConfirmDialogModel = {
            inputs: {
                heading: 'Monitoring.deleteTopic',
                message: this.localizationService.transform('Monitoring.confirmDeleteTopicMessage', { name: topic.name }),
                alertMessage: this.localizationService.transform('Monitoring.confirmDeleteTopicAlert'),
                alertType: messageSeverityEnum.warning,
                okLabel: 'Core.button.delete',
                id: 'TopicsDelete'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.deleteTopic(topic).subscribe();
                        if (this.focusedTopic && topic.id === this.focusedTopic.id && navigate) {
                            this.monitoringRoutingService.gotoTopics();
                        }
                    } else {
                        this.refreshAllSubject.next();
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    public confirmCopyTopic(topic: MentionSearchModel|MentionSearchListItemModel): void {
        const inputConfig: NewResourceDialogModel = {
            inputs: {
                heading: 'Monitoring.copyTopic',
                initialValue: topic.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: topic.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                formValidator: (name) => {
                    if (this.topics.find(t => t.name === name.trim())) {
                        return [
                            'Monitoring.topics.saveTopicsDuplicateNameErrorMessage'
                        ];
                    } else {
                        return [];
                    }
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    return this.getTopic(topic.id).pipe(
                        mergeMap( model => {
                            model.name = name;
                            model.groupId = group?.id;
                            model.isFavorite = false;
                            delete model.id;
                            return this.createTopic(model, false);
                        })
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
    }

    getTagToTopics(): Observable<Map<number, MentionSearchListItemModel[]>> {
        return this.tagToTopicsMap$;
    }
    getTaggingTopicsCount(): Observable<number> {
        return this.taggingTopicsCountSubject;
    }
    getTaggingTopicsLimitReached(): Observable<boolean> {
        return this.taggingTopicsLimitReached$;
    }

    getAutoSavingTopicsLimitReached(): Observable<boolean> {
        return this.autoSavingLimitReached$;
    }

    /**
     * Add detailed geographic region info to current topic if we have just basic geographic region info
     */
    private mapRawRegionsToComplexRegions(
        topic: MentionSearchModel
    ): Observable<JournodexGeographyModel[]> {
        const query = topic.searchCriteria.query;
        const rawMergedRegions: GeographyQueryModel = this.journodexService.mergeGeographyQueryModels(
            query.rawIncludedRegions,
            query.rawExcludedRegions
        );

        const request: Observable<JournodexGeographyModel[]> = this.journodexService.mapToComplexRegionInfo(rawMergedRegions);
        return request;
    }

    /**
     * Update the geographic information in a search criteria with proper details
     *
     * When we retrieve a save search from the API, the geographic information is minimal. This function
     * processes results from Journodex to amplify the geographic information to the detail we need going forward.
     * Primarily this is needed when editing an existing search details so we can show the geographic information
     * in the INCLUDE/EXCLUDE Region section.
     *
     * @param regionInfo  List of complex geographic data retrieved from Journodex
     * @param topic  Search to be updated with geographic details
     */
    private updateMentionSearchGeographicInformation(
        regionInfo: JournodexGeographyModel[],
        topic: MentionSearchModel
    ): void {
        const query = topic.searchCriteria.query;
        for (const region of regionInfo) {
            if (region.isInRawList(query.rawIncludedRegions)) {
                query.includedRegions.push(region);
            }

            if (region.isInRawList(query.rawExcludedRegions)) {
                query.excludedRegions.push(region);
            }
        }

        // We can get rid of the basic geo information so we don't try to get the complex details again
        query.rawExcludedRegions = null;
        query.rawIncludedRegions = null;
    }

    /**
     * b.e stores source group ids in search query model, and this method generates their display strings
     */
    private generateSourceGroupDisplayString() {
        if (this.sourceGroups && this.focusedTopic?.searchCriteria?.query) {
            this.sourceGroups.forEach(group => {
                const sources = this.focusedTopic.searchCriteria.query.includedSources.concat(this.focusedTopic.searchCriteria.query.excludedSources);
                sources.forEach(source => {
                    if (source.entityName === group.id.toString()) {
                        source.displayString = group.name;
                    }
                });
            });
        }
    }

    /**
     * Check for unsaved changes, except when performing account search.
     * Users can't creating Topics on saved content, therefore no saved changes gaurd is required
     */
    canLeaveCurrentTopic(currentSearch: MentionSearchModel, routeType?: RouteType): boolean {
        if (currentSearch?.searchCriteria?.isAccountSearch) {
            return true;
        }
        // For the topic results page, dateFacets, dateQuery and showSimilar are ignored since those
        // settings drive the runtime filter rather than the saved topic for topic search. page/pageSize/
        // sort are always ignored since they reflect runtime pagination state, not saved topic content.
        return routeType === RouteType.topic
            ? currentSearch?.equals(this.cachedTopic, true, true, true, true)
            : currentSearch?.equals(this.cachedTopic, false, false, false, true);
    }


    showExitDialog(
        heading: string = 'DeactivateGuard.heading',
        message: string = 'DeactivateGuard.message',
        okayLabel = 'Monitoring.confirm'
    ): Observable<boolean> {
        if (!this.isDirty) {
            return of(true);
        }

        return new Observable<boolean>(
            (observer: Observer<boolean>) => {
                const confirmConfig: ConfirmDialogModel = {
                    inputs: {
                        id: 'MonitoringSearch',
                        heading: heading,
                        messageHtml: message,
                        okLabel: okayLabel
                    },
                    outputs: {
                        dialogClose: async (confirmed: boolean) => {
                            if (confirmed) {
                                this.setDirtyFlag(false);
                            }
                            observer.next(confirmed);
                            observer.complete();
                        }
                    }
                };
                this.coreDialogService.open(ConfirmDialogComponent, confirmConfig);
            }
        );
    }

    private currentMode$(): Observable<TopicMode> {
        return this.monitoringRoutingService.getCurrentRoute().pipe(
            map(route => {
                const topicRoutes = [
                    RouteType.topic,
                    RouteType.search,
                    RouteType.updateSearch,
                    RouteType.results,
                    RouteType.adHocAnalyticsDashboard
                ];
                return (
                    topicRoutes.includes(route.type)
                        ? TopicMode.topics
                        : TopicMode.other
                );
            })
        );
    }

    /**
     * Helper function for handling api requests
     */
    private request<T>(request: Observable<T>): Observable<T> {
        let savedValue: T;

        const req = request.pipe(
            tap(value => {
                savedValue = value;
            }),
            share({
                connector: () => new AsyncSubject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            }),
            map(() => savedValue)
        );

        req.subscribe();

        return req;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
